<!-- Localized -->
<template>
  <ModalContent
    ref="status-permissions-content"
    :name="name"
    :title="$t('pages.workflow_engine.dashboard_assignment')"
    :submit-button-text="submitButtonText"
    width="1100px"
    v-bind="$attrs"
    v-on="$listeners"
    @submit="onSubmit"
    @hidden="onContentHide"
    :disabled="disabled"
  >
    <template #content>
      <Loader v-if="loading" />
      <div v-else class="grid grid-cols-2 auto-rows-fr gap-8">
        <ListView
          selectKey="selected"
          valueKey="user_name"
          :title="`${clientName} ${$t('components.roles_permissions.users')}`"
          :listItems="filteredUserList"
          :disableCheckbox="disabled"
          show-select-all
          show-search
          class="h-full overflow-y-auto"
          @handleSelect="selectAllUserList"
          @rowClick="onRowClick($event, 'client')"
          @handleFilterResult="handleFilterResult($event, 'client')"
        />
        <ListView
          selectKey="selected"
          valueKey="title"
          :title="`${clientName} ${$t('pages.workflow_engine.groups')}`"
          :listItems="filteredClientGroups"
          :disableCheckbox="disabled"
          show-select-all
          show-search
          class="h-full overflow-y-auto"
          @handleSelect="selectAllClientGroups"
          @rowClick="onRowClick($event, 'tenant')"
          @handleFilterResult="handleFilterResult($event, 'tenant')"
        />
      </div>
    </template>
  </ModalContent>
</template>

<script>
import ModalContent from "@shared/modal-content";
import ListView from "@shared/components/lists";
import { sortItemsAlphabetically } from "@shared/utils/functions";
import Loader from "@shared/components/WrappedLoader";
import { caseEntityTypesMap } from "@shared/modules/localization/utils/caseEntityTranslation/constants";
import { translateEntity } from "@shared/modules/localization/utils/caseEntityTranslation";

export default {
  name: "GroupsModal",

  components: {
    ModalContent,
    ListView,
    Loader,
  },

  props: {
    /**
     * Holds all tenant and users groups in two arrays
     * @property {Object[]} users
     * @property {Object[]} groups
     */
    groups: {
      type: Object,
      required: true,
    },
    /**
     * Holds an array of assigned group ids (tenant and client)
     * By default all groups are assigned
     */
    value: {
      type: Array,
      required: true,
    },
    name: {
      type: String,
      default: "status-permissions-content",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    
    clientName: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      searchValues: {
        tenant: "",
        client: "",
      },
    };
  },

  computed: {
    submitButtonText() {
      return this.$t('actions.save');
    },
    

    filteredUserList() {
      return this.userLists.filter((el) =>
        el.user_name?.toLowerCase()?.includes(this.searchValues?.client?.toLowerCase())
      );
    },

    userLists() {
      return (
        this.groups.users
          ?.map((el) => ({ ...el, selected: this.value.includes(el.id) }))
          ?.filter((el) => el.user_name) // Filter out items without name
          ?.sort((a, b) => sortItemsAlphabetically([a, b], "user_name"))
          ?.sort((a, b) => b.selected - a.selected) ?? []
      );
    },


    filteredClientGroups() {
      return this.clientGroups.filter((el) =>
        el.title?.toLowerCase()?.includes(this.searchValues?.tenant?.toLowerCase())
      );
    },

    clientGroups() {
      if (!this.groups.client || !Array.isArray(this.groups.client)) {
        return [];
      }
      return (
        this.groups.client
          ?.map((el) => ({ ...el, selected: this.value.includes(el.id) }))
          ?.filter((el) => el.title) // Filter out items without name
          ?.sort((a, b) => sortItemsAlphabetically([a, b], "title"))
          ?.sort((a, b) => b.selected - a.selected) ?? []
      );

    },
  },

  methods: {
    translateEntity(entity) {
      return translateEntity(entity, caseEntityTypesMap.GROUP);
    },
    handleFilterResult(event, type) {
      this.searchValues[type] = event;
    },
    onRowClick({ row }) {
    
        this.$emit("input", row.id);
      
    },
    onGroupSelect(payload) {
      this.$emit("input", payload);
    },
    onSubmit() {
      this.$emit("onSubmit");
      this.onContentHide();
    },
    selectAllClientGroups(event) {
      const groupIds = this.groups.client?.map((el) => el.id);
      
      if (event) {
        // Select all groups - add to existing selection
        const newSelection = [...new Set([...this.value, ...groupIds])];
        this.$emit("input", newSelection);
      } else {
        // Unselect all groups - remove only group IDs
        const filteredSelection = this.value.filter(id => !groupIds.includes(id));
        this.$emit("input", filteredSelection);
      }
    },
    selectAllUserList(event) {
      const userIds = this.groups.users?.map((el) => el.id);
      
      if (event) {
        // Select all users - add to existing selection
        const newSelection = [...new Set([...this.value, ...userIds])];
        this.$emit("input", newSelection);
      } else {
        // Unselect all users - remove only user IDs
        const filteredSelection = this.value.filter(id => !userIds.includes(id));
        this.$emit("input", filteredSelection);
      }
    },
    onContentHide() {
      this.searchValues.tenant = "";
      this.searchValues.client = "";
    },
    showModal() {
      this.$refs["status-permissions-content"]?.showModal();
    },
    hideModal() {
      this.$refs["status-permissions-content"]?.hideModal();
    },
  },
};
</script>
